/**
 * Filtro de estrutura (Barragem A/B/C) — GUPM-134.
 * Pills segmentadas: Todas · Barragem A · B · C, cada uma com contagem.
 * Componente controlado, reusado em Mapa (M2) e Instrumentação (M6).
 *
 * GUPM-194: no mapa, as pills de barragem espelham o STATUS da estrutura (mesmas
 * cores do header, via `statusByDam` + `STATUS_ESTRUTURA_UI`) e usam a densidade
 * compacta (`size="sm"`) das demais pills do mapa. "Todas" segue neutra (não há
 * chip "Geral" no header). Sem "bolinha" — a cor vem do próprio contorno/texto.
 */
import { ALL_DAMS, type DamSelection } from '@/domain/instrumentos/dam-filter';
import type { DamOption } from '@/hooks/useDams';
import type { StatusEstrutura } from '@/domain/estrutura';
import { STATUS_ESTRUTURA_UI } from './tokens';
import { rotuloDaEstrutura } from '@/domain/estrutura/rotulo';

export interface StructureFilterProps {
  dams: DamOption[];
  /** Contagem de instrumentos por `damId` (chave sempre presente, mesmo `0`). */
  counts: Record<string, number>;
  selected: DamSelection;
  onSelect: (selection: DamSelection) => void;
  className?: string;
  /**
   * GUPM-194: status consolidado por `damId` (mesma fonte do header, `useDamScope`).
   * Quando fornecido, cada pill de barragem é colorida pelo seu status. "Todas"
   * permanece neutra. Ausente → estilo neutro (amarelo/surface), como no M6.
   */
  statusByDam?: Record<string, StatusEstrutura>;
  /** Densidade: `'md'` (padrão, M6) ou `'sm'` (mapa — do tamanho das pills de camada). */
  size?: 'sm' | 'md';
}

interface Pill {
  key: DamSelection;
  label: string;
  count: number;
  /** Status da barragem (undefined para "Todas" ou quando `statusByDam` ausente). */
  status?: StatusEstrutura;
}

export function StructureFilter({
  dams,
  counts,
  selected,
  onSelect,
  className = '',
  statusByDam,
  size = 'md',
}: StructureFilterProps) {
  const total = dams.reduce((sum, d) => sum + (counts[d.id] ?? 0), 0);
  const pills: Pill[] = [
    { key: ALL_DAMS, label: 'Todas', count: total },
    ...dams.map((d) => ({
      key: d.id as DamSelection,
      label: rotuloDaEstrutura(d.code) ?? d.code,
      count: counts[d.id] ?? 0,
      status: statusByDam?.[d.id],
    })),
  ];

  const sizeCls = size === 'sm' ? 'px-2.5 py-1 rounded-md text-[11px]' : 'px-3 py-1.5 rounded-lg text-sm';

  return (
    <div role="group" aria-label="Filtrar por estrutura" className={`flex flex-wrap gap-2 ${className}`}>
      {pills.map((pill) => {
        const active = pill.key === selected;
        const ui = pill.status ? STATUS_ESTRUTURA_UI[pill.status] : null;

        // Pill colorida por status (barragem no mapa): cor sempre visível, seleção
        // acende o fundo tinto + contorno pleno; não-selecionada fica esmaecida.
        if (ui) {
          return (
            <button
              key={pill.key}
              type="button"
              aria-pressed={active}
              data-status={pill.status}
              onClick={() => onSelect(pill.key)}
              className={`${sizeCls} font-bold border transition ${active ? '' : 'opacity-70 hover:opacity-100'}`}
              style={{
                color: ui.color,
                backgroundColor: active ? ui.bg : 'transparent',
                borderColor: active ? ui.color : ui.border,
              }}
            >
              {pill.label}
              <span className="ml-1.5 text-xs font-semibold" style={{ opacity: 0.7 }} aria-hidden>
                {pill.count}
              </span>
            </button>
          );
        }

        // Pill neutra (M6, ou "Todas"): estilo amarelo/surface original.
        return (
          <button
            key={pill.key}
            type="button"
            aria-pressed={active}
            onClick={() => onSelect(pill.key)}
            className={`${sizeCls} font-bold border transition ${
              active
                ? 'bg-jf-yellow text-jf-bg border-jf-yellow'
                : 'bg-jf-surface text-jf-muted border-jf-border hover:text-white'
            }`}
          >
            {pill.label}
            <span
              className={`ml-1.5 text-xs font-semibold ${active ? 'text-jf-bg/70' : 'text-jf-dim'}`}
              aria-hidden
            >
              {pill.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
